import React from 'react';
import { Col, Row } from 'antd';
import { ResponsiveBar } from '@nivo/bar';
import Helpers from '../../../../helpers';
import { ResponsiveLine } from '@nivo/line';
import styles from './style.module.scss';
import PropTypes from 'prop-types';

const BarChangeChart = ({
  data,
  format = (v) => v,
  keys = [],
  lineItemWidth = 100,
  translateX = 160,
  hideLine = false,
  itemWidth = 100,
  translateXBar = -50,
  lineMargins = { top: 50, right: 155, bottom: 100, left: 85 },
  lineAxisRightTickPadding = 25,
  lineTickSize = 5,
  lineFormat,
  lineMinValue,
  hideMarker,
  className = '',
  formatTooltip = (v) => v,
  lineColors,
  layout = 'vertical',
  margin = { top: 50, right: 130, bottom: 100, left: 60 },
  hideLegend = false,
  hideTooltipKey = false,
}) => {
  return (
    <Row className={`${styles.barChange} ${className}`}>
      <Col span={24}>
        <ResponsiveBar
          data={data.barChart}
          layout={layout}
          keys={keys}
          indexBy='date'
          colors={({ id, data }) => String(data[`${id}Color`])}
          margin={margin}
          padding={0.15}
          innerPadding={8}
          groupMode='grouped'
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          height={400}
          maxValue={data.maxValue + 10}
          minValue={0}
          tooltip={({ data, id, color, indexValue }) => {
            console.log('iddd', id);
            return (
              <div
                style={{
                  background: 'white',
                  padding: '9px 12px',
                  border: '1px solid #ccc',
                }}
              >
                <div>{indexValue}</div>
                <div
                  key={color.id}
                  style={{
                    color: color,
                    padding: '3px 0',
                  }}
                >
                  {!hideTooltipKey && <strong>{id}</strong>} {' '}
                   {formatTooltip(format(data[id]))}
                </div>
              </div>
            );
          }}
          defs={[
            {
              id: 'dots',
              type: 'patternDots',
              background: 'inherit',
              color: '#38bcb2',
              size: 4,
              padding: 1,
              stagger: true,
            },
            {
              id: 'lines',
              type: 'patternLines',
              background: 'inherit',
              color: '#eed312',
              rotation: -45,
              lineWidth: 6,
              spacing: 10,
            },
          ]}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 1.6]],
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            format: (tick) =>
              Helpers.hideTicksByCount(data.tableData, tick) ? tick : '',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legendPosition: 'middle',
            legendOffset: 32,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legendPosition: 'middle',
            legendOffset: -40,
            format,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{
            from: 'color',
            modifiers: [['darker', '1.4']],
          }}
          legends={
            !hideLegend
              ? [
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom',
                    direction: 'row',
                    justify: false,
                    translateX: translateXBar,
                    translateY: 100,
                    itemsSpacing: 2,
                    itemWidth: itemWidth,
                    itemHeight: 20,
                    itemDirection: 'left-to-right',
                    itemOpacity: 0.85,
                    symbolSize: 20,
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemOpacity: 1,
                        },
                      },
                    ],
                  },
                ]
              : []
          }
          role='application'
          ariaLabel='Nivo bar chart demo'
          barAriaLabel={function (e) {
            return (
              e.id + ': ' + e.formattedValue + ' in country: ' + e.indexValue
            );
          }}
          layers={['grid', 'axes', 'bars', 'markers', 'legends']}
          labelFormat={format}
          tooltipFormat={format}
        />
      </Col>
      {!hideLine && (
        <Col span={24} className={styles.barChangeLine}>
          <ResponsiveLine
            colors={lineColors || ['#1f77b4', '#f1e15b']}
            data={data.barChangeLine}
            margin={{
              top: 50,
              right: 155,
              bottom: 100,
              left: 85,
              ...lineMargins,
            }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: lineMinValue ?? 'auto',
              max: 'auto',
              stacked: false,
              reverse: false,
            }}
            yFormat=' >-.2f'
            curve='monotoneX'
            enableSlices='x'
            sliceTooltip={({ slice }) => {
              const date = slice.points[0].data.x;
              return (
                <div
                  style={{
                    background: 'white',
                    padding: '9px 12px',
                    border: '1px solid #ccc',
                  }}
                >
                  <div>{date}</div>
                  {slice.points.map((point) => {
                    const tooltipKeys = point?.data?.tooltipKeys || [];
                    return (
                      <div key={point.id}>
                        {tooltipKeys.map(({ key, value, color, percent }) => {
                          return (
                            <p
                              style={{
                                color: color,
                                padding: '3px 0',
                              }}
                            >
                              <strong>{key}</strong> {value}
                              {percent}
                            </p>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            }}
            enableGridX={false}
            enableGridY={false}
            lineWidth={2}
            pointSize={5}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            useMesh={true}
            height={400}
            axisTop={null}
            axisRight={{
              orient: 'right',
              tickSize: lineTickSize,
              tickPadding: lineAxisRightTickPadding,
              tickRotation: 0,
              legend: '',
              legendOffset: 0,
              format: lineFormat || format,
            }}
            axisBottom={null}
            axisLeft={null}
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'bottom',
                direction: 'row',
                justify: false,
                translateX: translateX,
                translateY: 100,
                itemsSpacing: 2,
                itemWidth: lineItemWidth,
                itemHeight: 20,
                itemDirection: 'left-to-right',
                itemOpacity: 0.85,
                symbolSize: 20,
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemOpacity: 1,
                    },
                  },
                ],
              },
            ]}
            markers={
              !hideMarker
                ? [
                    {
                      axis: 'y',
                      value: 0,
                      lineStyle: { stroke: '#000', strokeWidth: 1 },
                      legendOrientation: 'vertical',
                    },
                  ]
                : []
            }
          />
        </Col>
      )}
    </Row>
  );
};

BarChangeChart.propTypes = {
  lineItemWidth: PropTypes.number,
  translateXLine: PropTypes.number,
};

export default BarChangeChart;
